import { DomainListItemCard } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteFulfillment } from "@/hooks/fulfillments/delete.hooks";
import { useGetHotelRoom } from "@/hooks/hotel-rooms";
import { useGetServiceProvider } from "@/hooks/service-providers/get.hooks";
import { useGetService } from "@/hooks/services";
import { dateToString } from "@/lib/utils";
import { HotelFulfillment, ServiceFulfillment } from "@/models";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export function HotelFulfillmentView({
  fulfillment,
  isTable,
  reservationId,
}: {
  fulfillment: HotelFulfillment;
  reservationId: string;
  isTable: boolean;
}) {
  const { t } = useTranslation();
  const { data: hotelRoom, isLoading } = useGetHotelRoom(
    fulfillment.hotelId?.toString() ?? "",
    fulfillment.hotelRoomId?.toString() ?? "",
  );
  const { mutateAsync } = useDeleteFulfillment();
  const deleteFulfillment = () =>
    mutateAsync({
      id: fulfillment.id.toString(),
      reservationId,
      type: "accommodation",
    });
  return (
    <DomainListItemCard
      table={isTable}
      domain="reservations.fulfillments"
      onDelete={deleteFulfillment}
      title={
        isLoading ? (
          <Skeleton className="h-7 w-2/3" />
        ) : (
          `${hotelRoom?.hotelName ?? "-"} (${hotelRoom?.designation ?? "-"})`
        )
      }
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={fulfillment.value}
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/reservations/$id/fulfillments/$fid/edit"
          params={{ id: reservationId, fid: fulfillment.id ?? "" }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          label: t("reservations.fulfillments.table.dueDate"),
          value: dateToString(fulfillment.dueDate),
        },
        {
          label: t("reservations.fulfillments.table.state"),
          value: fulfillment.state
            ? t(`reservations.fulfillments.state.${fulfillment.state}`)
            : "-",
        },
        {
          label: t("reservations.fulfillments.table.agencyCharge"),
          value: fulfillment.agencyCharge ?? "-",
        },
        {
          label: t("reservations.fulfillments.table.expenses"),
          value: fulfillment.expenses ?? "-",
        },
        {
          label: t("reservations.fulfillments.table.invoiceNumber"),
          value: fulfillment.invoiceNumber ?? "-",
        },
      ]}
      // onDelete={deletePayment}
    />
  );
}

export function ServiceFulfillmentView({
  fulfillment,
  isTable,
  reservationId,
}: {
  fulfillment: ServiceFulfillment;
  reservationId: string;
  isTable: boolean;
}) {
  const { t } = useTranslation();
  const { data: service, isLoading: serviceLoading } = useGetService(
    fulfillment.serviceId?.toString() ?? "",
    {
      enabled: typeof fulfillment.serviceId === "number",
    },
  );
  const { data: serviceProvider, isLoading: serviceProviderLoading } =
    useGetServiceProvider(
      "service",
      fulfillment.serviceProviderId?.toString() ?? "",
      {
        enabled: typeof fulfillment.serviceProviderId === "number",
      },
    );

  const { mutateAsync } = useDeleteFulfillment();
  const deleteFulfillment = () =>
    mutateAsync({
      id: fulfillment.id.toString(),
      reservationId,
      type: "service",
    });
  return (
    <DomainListItemCard
      table={isTable}
      onDelete={deleteFulfillment}
      domain="reservations.fulfillments"
      title={
        serviceLoading && typeof fulfillment.serviceId === "number" ? (
          <Skeleton className="h-7 w-2/3" />
        ) : (
          (service?.name ?? "-")
        )
      }
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={
        serviceProviderLoading &&
        typeof fulfillment.serviceProviderId === "number" ? (
          <Skeleton className="h-7 w-2/3" />
        ) : (
          (serviceProvider?.provider?.name ?? "-")
        )
      }
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/reservations/$id/fulfillments/$fid/edit"
          params={{ id: reservationId, fid: fulfillment.id ?? "" }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          label: t("reservations.fulfillments.table.value"),
          value: fulfillment.value,
        },
        {
          label: t("reservations.fulfillments.table.dueDate"),
          value: dateToString(fulfillment.dueDate),
        },
        {
          label: t("reservations.fulfillments.table.state"),
          value: fulfillment.state
            ? t(`reservations.fulfillments.state.${fulfillment.state}`)
            : "-",
        },
        {
          label: t("reservations.fulfillments.table.agencyCharge"),
          value: fulfillment.agencyCharge ?? "-",
        },
        {
          label: t("reservations.fulfillments.table.expenses"),
          value: fulfillment.expenses ?? "-",
        },
        {
          label: t("reservations.fulfillments.table.invoiceNumber"),
          value: fulfillment.invoiceNumber ?? "-",
        },
      ]}
      // onDelete={deletePayment}
    />
  );
}
